import type { ReactNode } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { TwoColumnsWithFooterShell } from "@/components/app-layout/two-columns-with-footer-shell";
import { AppFooter } from "@/components/app-layout/app-footer";
import CreateWorkspaceUrl from "@/assets/create-workspace-illustration.svg";

type AuthShellProps = {
  children: ReactNode;
  newUser: boolean;
};

/**
 * Shared layout for all authentication pages (login, signup, etc.)
 *
 * Uses TwoColumnShell directly:
 * - left: auth form inside a Card
 * - right: marketing copy (hidden on mobile)
 * - footer: shared AppFooter
 */
export function AuthShell({ children, newUser }: AuthShellProps) {
  return (
    <TwoColumnsWithFooterShell
      left={
        <Card className="w-full max-w-md shadow-sm">
          <CardContent className="p-6 md:p-8">{children}</CardContent>
        </Card>
      }
      right={
        newUser ? (
          <div className="hidden flex-col justify-center space-y-4 md:flex">
            <h1 className="text-2xl font-bold">Ready to get to work?</h1>

            <p className="text-muted-foreground text-sm leading-relaxed">
              Kairo is a collaborative workspace designed for software product teams.
            </p>

            <p className="text-muted-foreground text-sm leading-relaxed">
              Built from the ground up with AI at its core, it redefines project ideation,
              specification design, technical architecture as well as AI prototyping and hand-off.
            </p>

            <h2 className="text-xl font-medium">Let's begin.</h2>
          </div>
        ) : (
          <div className="hidden w-full items-center justify-center lg:flex">
            <CreateWorkspaceUrl className="max-h-105 w-full max-w-lg object-contain" />
          </div>
        )
      }
      footer={<AppFooter />}
    />
  );
}
